import * as PIXI from "pixi.js";
import {Animatable} from "./Animatable";
import {Container} from "./Container";
import {ContainerExtensions} from "./ContainerExtensions";
import {ColorUtil, IPoint} from "./Geom";
import {Mix} from "./Reflection";
import {Scalable} from "./Scalable";

function ConvertStyle(style: BitmapTextStyle): PIXI.extras.BitmapTextStyle {
    if (style.color && !style.tint) {
        style.tint = ColorUtil.toNumber(style.color);
    }
    return style;
}

export class BitmapText extends Mix(PIXI.extras.BitmapText, ContainerExtensions, Scalable, Animatable) {

    get fontSize(): number {
        return (this.font as any).size;
    }

    set fontSize(value: number) {
        this.font = {
            name: (this.font as any).name,
            size: value,
        };
        if (this._htmlText) {
            this.htmlText = this._htmlText;
        }
    }

    get lineHeight(): number {
        return this._lineHeight;
    }

    set lineHeight(value: number) {
        this._lineHeight = value;
    }

    get htmlFormat(): IHtmlFormat | undefined {
        return this._htmlFormat;
    }

    set htmlFormat(value: IHtmlFormat | undefined) {
        this._htmlFormat = value;
    }

    get htmlText(): string {
        return this._htmlText || this.text;
    }

    set htmlText(value: string) {
        value = value || "";
        this._htmlText = value;

        value = value.replace(/<\s*br\s*\/?\s*>/ig, "\n");

        this._htmlTextDom = new DOMParser().parseFromString(`<root>${value}</root>`, "application/xml");
        this.text = this._htmlTextDom.documentElement.textContent || "";
    }

    get textAreaWidth(): number {
        return this.maxWidth;
    }

    set textAreaWidth(value: number) {
        this.maxWidth = value;
    }

    get textAreaHeight(): number {
        return this._textAreaHeight;
    }

    set textAreaHeight(value: number) {
        this._textAreaHeight = value;
    }

    private _lineHeight: number = NaN;
    private _htmlFormat?: IHtmlFormat;
    private _htmlText?: string;
    private _htmlTextDom?: Document;
    private _textAreaHeight: number = 0;
    private readonly _defaultTint: number = NaN;

    constructor(text: string, style: BitmapTextStyle, anchor?: IPoint) {
        super(style.htmlFormat && text.indexOf("<") >= 0 ? "" : text, ConvertStyle(style));

        this._defaultTint = style && !isNaN(style.tint!) ? style.tint! : 0xffffff;
        if (style && !isNaN(style.lineHeight!)) {
            this._lineHeight = style.lineHeight!;
        }
        if (anchor) {
            (this.anchor as any).set(anchor.x, anchor.y);
        }
        if (style.htmlFormat) {
            this.htmlFormat = style.htmlFormat;
            if (text.indexOf("<") >= 0) {
                this.htmlText = text;
            }
        }
    }

    public onLayout() {
        const a = this.anchor as IPoint;
        this.x += this.textAreaWidth * a.x;
        this.y += this.textAreaHeight * a.y;
    }

    public validateText() {
        this.validate();
    }

    public destroy(options?: PIXI.DestroyOptions | boolean): void {
        this.killTweens();
        super.destroy(options);
    }

    protected updateText(): void {

        let lineHeight: number;
        let fontData: any;
        if (!isNaN(this._lineHeight)) {
            fontData = PIXI.extras.BitmapText.fonts[(this.font as any).name];
            lineHeight = fontData.lineHeight;
            fontData.lineHeight = this._lineHeight * lineHeight;
        }

        super.updateText();

        if (fontData) {
            fontData.lineHeight = lineHeight!;
        }

        if (this._htmlTextDom) {
            this.applyHtmlFormat(this._htmlTextDom);
            this._htmlTextDom = undefined;
        }
    }

    private applyHtmlFormat(dom: Document): void {
        if (!this._htmlFormat) {
            return;
        }

        // @ts-ignore
        // IE11 requires the forth parameter entityReferenceExpansion
        const iterator = dom.createNodeIterator(dom.documentElement, NodeFilter.SHOW_TEXT, null, false);
        let node = iterator.nextNode();
        let i: number = 0;

        let lastCharX: number = NaN;
        while (node) {

            const tagName = node.parentNode!.nodeName;
            let length = node.textContent!.length; // replace(/[\n\r]/, '').length;
            const tagFormat = this._htmlFormat[tagName];
            let color = tagFormat ? ColorUtil.toNumber(tagFormat.color!) : NaN;
            const alpha = tagFormat && !isNaN(tagFormat.opacity!) ? tagFormat.opacity : 1;
            const handler = tagFormat ? tagFormat.handler : null;
            if (isNaN(color)) {
                color = this._defaultTint;
            }
            for (let j = i, n = Math.min(i + length, this.children.length); j < n; j++) {
                const child = this.getChildAt(j) as PIXI.Sprite;
                // Space character is removed at the end of text line
                if (child.x < lastCharX && child.width > 0 && child.height > 0) {
                    lastCharX = child.x;
                    if (n < this.children.length) {
                        length--;
                        n--;
                    }
                    j--;
                    continue;
                }
                lastCharX = child.x;
                child.tint = color;
                child.alpha = alpha!;
                if (handler) {
                    handler.call(this, child);
                }
            }

            i += length;
            node = iterator.nextNode();
        }
    }
}

export interface BitmapTextStyle {
    font: string | { name: string, size: number };
    /**
     * PIXI compatibility
     */
    tint?: number;
    color?: string | number;
    align?: BitmapTextAlign;
    htmlFormat?: IHtmlFormat;
    lineHeight?: number;
}

export type BitmapTextAlign = "left" | "center" | "right";

export interface IHtmlFormat {
    [tagName: string]: IHtmlTagFormat;
}

export interface IHtmlTagFormat {
    color?: string | number;
    opacity?: number;
    handler?: (char: PIXI.Sprite) => void;
}

export class MultiBitmapText extends Container {

    public readonly styles: MultiBitmapTextStyle[];
    public readonly textFields: BitmapText[] = [];

    constructor(text: string, styles: MultiBitmapTextStyle[], anchor?: IPoint) {
        super();

        this.styles = styles;
        const style0 = styles[0];
        for (const style of styles) {

            if (isNaN(style.offsetX!)) {
                style.offsetX = style.offset;
            }
            if (isNaN(style.offsetY!)) {
                style.offsetY = style.offset;
            }
            if (!style.align) {
                style.align = style0.align;
            }

            const t = new BitmapText("", style, anchor);
            if (!isNaN(style0.fontSize!)) {
                t.fontSize = style0.fontSize!;
            }

            this.textFields.push(this.addChildAt(t, 0));
        }
        this.applyStyles();

        if (text) {
            this.text = text;
        }
    }

    private applyStyles() {
        for (let i = 0; i < this.styles.length; i++) {

            const style = this.styles[i];
            const text = this.textFields[i];

            if (!isNaN(style.offsetX!)) {
                text.x = style.offsetX! * text.fontSize;
            }
            if (!isNaN(style.offsetY!)) {
                text.y = style.offsetY! * text.fontSize;
            }
            if (!isNaN(style.alpha!)) {
                text.alpha = style.alpha!;
            }
        }
    }

    get align(): BitmapTextAlign {
        return this.textFields.length > 0 ? this.textFields[0].align as BitmapTextAlign : "left";
    }

    set align(value: BitmapTextAlign) {
        for (const textField of this.textFields) {
            textField.align = value;
        }
        this.applyStyles();
    }

    get fontSize(): number {
        return this.textFields.length > 0 ? this.textFields[0].fontSize : 0;
    }

    set fontSize(value: number) {
        for (const textField of this.textFields) {
            textField.fontSize = value;
        }
        this.applyStyles();
    }

    get lineHeight(): number {
        return this.textFields.length > 0 ? this.textFields[0].lineHeight : 0;
    }

    set lineHeight(value: number) {
        for (const textField of this.textFields) {
            textField.lineHeight = value;
        }
    }

    get text(): string {
        return this.textFields.length > 0 ? this.textFields[0].text : "";
    }

    set text(value: string) {
        for (const textField of this.textFields) {
            textField.text = value;
        }
    }

    get htmlFormat(): IHtmlFormat | undefined {
        return this.textFields.length > 0 ? this.textFields[0].htmlFormat : undefined;
    }

    set htmlFormat(value: IHtmlFormat | undefined) {
        for (const textField of this.textFields) {
            textField.htmlFormat = value;
        }
    }

    get htmlText(): string {
        return this.textFields.length > 0 ? this.textFields[0].htmlText : "";
    }

    set htmlText(value: string) {
        for (const textField of this.textFields) {
            textField.htmlText = value;
        }
    }
}

export interface MultiBitmapTextStyle extends BitmapTextStyle {
    fontSize?: number;
    alpha?: number;
    offset?: number;
    offsetX?: number;
    offsetY?: number;
}
